import React, { useState, useEffect, useMemo } from "react";

import { useAppDispatch, useAppSelector } from "../../redux/hooks/redux-hooks";
import { fetchSegmentLOBs } from "../../redux/actions/SegmentLOBActions";
import { fetchCustomerTypes } from "../../redux/actions/CustomerTypeActions";
import { fetchSegmentClassifications } from "../../redux/actions/SegmentClassificationActions";
import { fetchSegmentClassificationMap } from "../../redux/actions/SegmentClassificationMapActions";
import { displayDateFormart, isValidArr } from "../../utility/utils";
import EnhancedTable from "../table/Table";
import PageTitle from "../PageTitle";
import PageSearch from "../PageSearch";
import DialogBox from "../../components/dialog-box/DialogBox";
import SegmentClassificationMapForm from "./SegmentClassificationMapForm";
import Loader from "../../components/loader/Loader";
import { SegmentClassificationMapModel } from "../../models/SegmentClassificationMapModel";

export type Mode = "add" | "delete";

type iData = {
  id: string;
  segmentLOB: string;
  customerType: string;
  segmentClassification: string;
  createdDate: string;
  // status: string;
};

const SegmentClassificationMapPage = () => {
  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("add");
  const [editData, setEditData] = useState<SegmentClassificationMapModel>();
  const [rootData, setRootData] = useState<iData[]>([]);
  const [filteredData, setFilteredData] = useState<iData[]>([]);

  const allCustomerTypes = useAppSelector(
    (state) => state.customerType.all_CustomerTypes
  );
  const allSegmentLOBs = useAppSelector(
    (state) => state.segmentLOB.all_SegmentLOBs
  );
  const allSegmentClassificationMap = useAppSelector(
    (state) => state.segmentClassificationMap.all_Segment_Classification_Map
  );
  const isSegmentClassificationMapsFetching = useAppSelector(
    (state) =>
      state.segmentClassificationMap.isSegmentClassificationMapsFetching
  );

  const columns = useMemo(() => {
    return [
      {
        id: "segmentLOB",
        numeric: false,
        disablePadding: false,
        label: "Segment LOB",
      },
      {
        id: "customerType",
        numeric: false,
        disablePadding: false,
        label: "Customer Type",
        width: 200,
      },
      {
        id: "segmentClassification",
        numeric: false,
        disablePadding: false,
        label: "Segment Classification",
      },
      {
        id: "createdDate",
        numeric: false,
        disablePadding: false,
        label: "Created Date",
        width: 150,
      },
      // {
      //   id: "status",
      //   numeric: false,
      //   disablePadding: false,
      //   label: "Status",
      // },
    ];
  }, []);

  //To fetch all Customertypes, SetgmentLOBs, Segment Classifications and Segment Classifications Map
  useEffect(() => {
    dispatch(fetchSegmentLOBs());
    dispatch(fetchCustomerTypes());
    dispatch(fetchSegmentClassifications());
    dispatch(fetchSegmentClassificationMap());
  }, [dispatch]);

  useEffect(() => {
    setRootData(() =>
      allSegmentClassificationMap.map((rec) => {
        const segmentLOB = allSegmentLOBs.find(
          (segmentRec) => segmentRec.segmentlobid === rec.segmentLobid
        );
        const customerType = allCustomerTypes.find(
          (customerTypeRec) =>
            customerTypeRec.customerTypeId === rec.customerTypeId
        );
        return {
          id: `${rec.segmentLobid}|${rec.customerTypeId}`,
          segmentLOB: segmentLOB?.name!,
          customerType: customerType?.name!,
          segmentClassification: rec.segmentClassification,
          createdDate: displayDateFormart(rec.createdDate),
          // status: rec.isActive ? "Active" : "Inactive",
        };
      })
    );
  }, [allCustomerTypes, allSegmentLOBs, allSegmentClassificationMap]);

  // To set filtered data on change root data
  useEffect(() => {
    setFilteredData(rootData);
  }, [rootData]);

  const handleEdit = (id: string | number) => {
    const idArr = id.toString().split("|");
    const editRec = allSegmentClassificationMap.find(
      (rec) =>
        rec.segmentLobid === +idArr[0] && rec.customerTypeId === +idArr[1]
    );
    setMode("delete");
    setEditData(editRec);
    setIsModalOpen(true);
  };

  const createHandler = () => {
    setMode("add");
    setEditData(undefined);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const searchHandler = (searchVal: string): void => {
    setFilteredData(() =>
      searchVal
        ? rootData.filter((info) =>
            Object.values(info).join("").toLowerCase().includes(searchVal)
          )
        : rootData
    );
  };

  return (
    <>
      {!isValidArr(allSegmentClassificationMap) &&
      isSegmentClassificationMapsFetching ? (
        <Loader />
      ) : null}
      <PageTitle
        title="Segment Classification Mapping"
        btnText="Add Segment Classification Mapping"
        onClick={createHandler}
      />
      <PageSearch
        placeholder="Search Segment Classification Maps"
        searchHandler={searchHandler}
      />
      <EnhancedTable
        columns={columns}
        data={filteredData}
        handleEdit={handleEdit}
        isEditable={false}
      />
      {isModalOpen && (
        <DialogBox
          isModalOpen={isModalOpen}
          modalTitle={
            mode === "add"
              ? "Create Segment Classification Mapping"
              : "Delete Segment Classification Mapping"
          }
          handleModalClose={handleModalClose}
        >
          <SegmentClassificationMapForm
            mode={mode}
            data={editData}
            handleModalClose={handleModalClose}
          />
        </DialogBox>
      )}
    </>
  );
};

export default SegmentClassificationMapPage;
